import React, { useMemo, useState } from 'react';
import {
  DEFAULT_PROFIT_INPUTS,
  PROFIT_LEVERS,
  calculateLeverImpacts,
  calculateProfitMetrics,
  calculateScenarioDeltas,
  formatMoney,
  formatMultiplier,
  formatNumber,
  formatPercent,
  getInsightState,
  getProfitDeltaState,
  getSliderFillPercent,
} from './profitControlLogic';
import { formatTrustedMessage } from '../../utils/htmlSecurity';
import './ProfitControl.css';

const leverIcons = {
  leads: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2 12l3-3.5 2.5 2.5L11 6l3 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  cr: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8l3.5 3.5L13 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  price: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 5v6M6 7h3.5a1.5 1.5 0 010 3H6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
  q: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2 8a6 6 0 016-6 6 6 0 015.2 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M14 5V2l-3 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 8a6 6 0 01-6 6 6 6 0 01-5.2-3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M2 11v3l3-3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  margin: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 13L13 3M10 3h3v3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="5" cy="11" r="1.5" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="11" cy="5" r="1.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
};

const formatLeverValue = (lever, value, locale) => {
  if (lever.format === 'money') {
    return formatMoney(value, locale);
  }

  if (lever.format === 'percent') {
    return formatPercent(value);
  }

  if (lever.format === 'multiplier') {
    return formatMultiplier(value);
  }

  return formatNumber(value, locale);
};

const getScenarioLabelText = (leverId, inputs, targets) => {
  const { leads, crPct, q, marginPct } = inputs;

  switch (leverId) {
    case 'leads':
      return `Получить ${targets.leads} заявок вместо ${leads}`;
    case 'crPct':
      return `Конверсия продаж ${crPct}% → ${targets.crPct}%`;
    case 'price':
      return `Поднять чек до ${targets.price}`;
    case 'q':
      return `Клиент покупает ${q}× → ${targets.q}× в год`;
    case 'marginPct':
      return `Маржинальность ${marginPct}% → ${targets.marginPct}%`;
    default:
      return '';
  }
};

const SCENARIO_NAMES = {
  leads: 'заявки',
  crPct: 'конверсию',
  price: 'средний чек',
  q: 'частоту покупок',
  marginPct: 'маржинальность',
};

const LEVER_LABELS = {
  leads: 'Заявки',
  crPct: 'Конверсию',
  price: 'Средний чек',
  q: 'Частоту покупок',
  marginPct: 'Маржинальность',
};

const LeverRow = ({ lever, value, impact, onChange, locale }) => {
  const fillPercent = getSliderFillPercent(value, lever.min, lever.max);

  return (
    <div className="pc-lever">
      <div className="pc-lever-top">
        <div className="pc-lever-icon" style={{ background: lever.track, color: lever.color }}>
          {leverIcons[lever.icon]}
        </div>
        <div className="pc-lever-info">
          <div className="pc-lever-name">{lever.label}</div>
          <div className="pc-lever-hint">{lever.hint}</div>
        </div>
        <div className="pc-lever-value">{formatLeverValue(lever, value, locale)}</div>
      </div>
      <input
        type="range"
        className="pc-slider-input"
        min={lever.min}
        max={lever.max}
        step={lever.step}
        value={value}
        onChange={(event) => onChange(lever.id, Number(event.target.value))}
        style={{
          background: `linear-gradient(to right, ${lever.color} ${fillPercent}%, var(--surface2) ${fillPercent}%)`,
        }}
      />
      <div className="pc-lever-impact">
        <span>{'Влияние на прибыль'}</span>
        <div className="pc-impact-bar">
          <div className="pc-impact-fill" style={{ width: `${impact}%`, background: lever.color }} />
        </div>
        <span>{impact}%</span>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, sub, explain }) => (
  <article className="pc-metric-card">
    <div className="pc-metric-label">{label}</div>
    <div className="pc-metric-value">{value}</div>
    <div className="pc-metric-sub">{sub}</div>
    {explain ? <p className="pc-metric-explain">{explain}</p> : null}
  </article>
);

const ProfitControl = () => {
  const [inputs, setInputs] = useState(DEFAULT_PROFIT_INPUTS);
  const [appliedScenario, setAppliedScenario] = useState(null);
  const locale = 'ru-RU';

  const metrics = useMemo(() => calculateProfitMetrics(inputs), [inputs]);
  const impacts = useMemo(() => calculateLeverImpacts(inputs), [inputs]);
  const scenarioData = useMemo(() => calculateScenarioDeltas(inputs), [inputs]);
  const insight = useMemo(
    () =>
      getInsightState({
        profit: metrics.profit,
        crPct: inputs.crPct,
        q: inputs.q,
        scenarioDeltas: scenarioData.deltas,
      }),
    [inputs.crPct, inputs.q, metrics.profit, scenarioData.deltas],
  );

  const deltaState = useMemo(
    () =>
      getProfitDeltaState({
        appliedScenario,
        profit: metrics.profit,
        baseProfit: metrics.baseProfit,
        scenarioName: appliedScenario ? SCENARIO_NAMES[appliedScenario] : null,
      }),
    [appliedScenario, metrics.baseProfit, metrics.profit],
  );

  const insightMessage = useMemo(() => {
    const vars = insight.vars?.bestLeverId
      ? { ...insight.vars, leverName: LEVER_LABELS[insight.vars.bestLeverId] }
      : insight.vars || {};
    return formatTrustedMessage(insight.message, vars);
  }, [insight]);

  const deltaMessage = useMemo(() => {
    if (!deltaState) {
      return null;
    }

    const delta = formatMoney(metrics.profit - metrics.baseProfit, locale);
    return formatTrustedMessage(deltaState.message, {
      ...deltaState.vars,
      delta,
      scenarioName: deltaState.vars?.scenarioName || '',
    });
  }, [deltaState, metrics.baseProfit, metrics.profit, locale]);

  const handleInputChange = (field, value) => {
    setInputs((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleScenarioApply = (leverId) => {
    const lever = PROFIT_LEVERS.find((item) => item.id === leverId);
    const target = scenarioData.targets[leverId];

    if (!lever || target === undefined) {
      return;
    }

    const nextValue = Math.min(Math.max(target, lever.min), lever.max);
    setInputs((current) => ({
      ...current,
      [leverId]: nextValue,
    }));
    setAppliedScenario(leverId);
  };

  const handleReset = () => {
    setInputs(DEFAULT_PROFIT_INPUTS);
    setAppliedScenario(null);
  };

  const getScenarioLabel = (leverId) => getScenarioLabelText(leverId, inputs, scenarioData.targets);

  return (
    <div className="pc-control">
      <div className="pc-main-layout">
        <section className="pc-card">
          <h2 className="pc-card-title">{'Рычаги вашего бизнеса'}</h2>
          {PROFIT_LEVERS.map((lever) => (
            <LeverRow
              key={lever.id}
              lever={lever}
              value={inputs[lever.id]}
              impact={impacts[lever.id]}
              onChange={handleInputChange}
              locale={locale}
            />
          ))}
        </section>

        <div className="pc-right-col">
          <section className="pc-profit-hero">
            <div className="pc-profit-label">{'Прибыль в месяц'}</div>
            <div className="pc-profit-value">{formatMoney(metrics.profit, locale)}</div>
            <div className="pc-profit-sub">
              {`выручка ${metrics.revenue} · маржа ${inputs.marginPct}%`}
            </div>
            {deltaState ? (
              <div className={`pc-profit-delta pc-profit-delta-${deltaState.variant}`}>
                {deltaMessage}
              </div>
            ) : null}
          </section>

          <div className="pc-formula-row">
            <span className="pc-formula-val">{formatNumber(inputs.leads, locale)}</span>
            <span className="pc-formula-op">{'лидов ×'}</span>
            <span className="pc-formula-val">{formatPercent(inputs.crPct)}</span>
            <span className="pc-formula-op">{'× чек'}</span>
            <span className="pc-formula-val">{formatNumber(inputs.price, locale)}</span>
            <span className="pc-formula-op">×</span>
            <span className="pc-formula-val">{inputs.q}</span>
            <span className="pc-formula-op">{'× марж.'}</span>
            <span className="pc-formula-val">{formatPercent(inputs.marginPct)}</span>
            <span className="pc-formula-op">{'= прибыль'}</span>
          </div>

          <div className="pc-metrics-grid">
            <MetricCard
              label={'Клиентов в месяц'}
              value={`${metrics.clients} ${'чел'}`}
              sub={`из ${inputs.leads} заявок купят`}
              explain={'Лиды × конверсию. Остальные не купили — их нужно возвращать или улучшать скрипт.'}
            />
            <MetricCard
              label={'Выручка в месяц'}
              value={formatMoney(metrics.revenue, locale)}
              sub={'деньги пришли на счёт'}
              explain={'Это ещё не прибыль — из неё нужно вычесть все расходы бизнеса.'}
            />
            <MetricCard
              label={'Ценность клиента (LTV)'}
              value={formatMoney(metrics.ltv, locale)}
              sub={`чек ${inputs.price} × ${inputs.q} покупок`}
              explain={'Сколько денег один клиент принесёт за все свои покупки. Чем выше — тем больше можно тратить на привлечение.'}
            />
            <MetricCard
              label={'Прибыль с 1 клиента'}
              value={formatMoney(metrics.ppc, locale)}
              sub={'чистыми после всех расходов'}
              explain={'Это максимум, сколько вы можете потратить на привлечение одного клиента и остаться в нуле.'}
            />
          </div>

          <section className="pc-scenario-card">
            <div className="pc-scenario-head">
              <h3 className="pc-scenario-title">{'Что будет если улучшить каждый рычаг'}</h3>
              {appliedScenario ? (
                <button type="button" className="pc-reset-btn" onClick={handleReset}>
                  {'↺ Сбросить'}
                </button>
              ) : null}
            </div>
            {PROFIT_LEVERS.map((lever) => {
              const delta = scenarioData.deltas[lever.id];

              return (
                <button
                  key={lever.id}
                  type="button"
                  className={`pc-scenario-row${appliedScenario === lever.id ? ' pc-scenario-row-active' : ''}`}
                  onClick={() => handleScenarioApply(lever.id)}
                >
                  <span className="pc-scenario-dot" style={{ background: lever.color }} />
                  <span className="pc-scenario-name">{getScenarioLabel(lever.id)}</span>
                  <span className={`pc-scenario-delta${delta >= 0 ? ' pc-scenario-delta-pos' : ' pc-scenario-delta-neg'}`}>
                    {delta >= 0 ? '+' : ''}
                    {formatMoney(delta, locale)}
                  </span>
                </button>
              );
            })}
            <p className="pc-scenario-hint">{'Нажмите строку — ползунок сдвинется к цели. Нажмите «Сбросить» — вернётся к исходным.'}</p>
          </section>

          <div className={`pc-insight pc-insight-${insight.variant}`} dangerouslySetInnerHTML={{ __html: insightMessage }} />
        </div>
      </div>
    </div>
  );
};

export default ProfitControl;
